import {createStore, type StoreApi} from 'zustand/vanilla';

import {keys, omit} from 'ramda';

import type {Nullish} from 'shared/web';

import {type ActionLockReason} from '../types/ActionLockState';
import type {ActionsAlignment} from '../types/ActionsAlignment';
import type {
  ActivePreset,
  CellsV5,
  ChoiceFilterOptions,
  ColumnsSetting,
  FeGridSettings,
  GetDataQueryResponse,
  Preset,
  PresetDisplayMode,
  SuggestionsOptions,
  TransformedDefinitionApiResponse,
  VirtualPreset,
} from '../types/Api';
import type {DataGridBulkActionsResponseBody} from '../types/api/bulkOperations';
import type {EagColumnState} from '../types/EagColumnState';
import type {Filters} from '../types/Filters';
import type {LoadedGridPageRequest} from '../types/LoadedGridPageRequest';
import type {QueryFilterObject} from '../types/QueryFilterObject';
import type {RowData} from '../types/RowData';
import type {SelectedCellRange} from '../types/SelectedCellRange';
import type {ToolpanelType} from '../types/ToolpanelType';
import {createActionLockActions} from './slices/createActionLockActions';
import {createColumnActions} from './slices/createColumnActions';
import {createCoreActions} from './slices/createCoreActions';
import {createDataConfigActions} from './slices/createDataConfigActions';
import {createDragActions} from './slices/createDragActions';
import {createKeyboardNavActions} from './slices/createKeyboardNavActions';
import {createMiscUiActions} from './slices/createMiscUiActions';
import {createResizeActions} from './slices/createResizeActions';
import {createSelectionActions} from './slices/createSelectionActions';
import {createServerDataActions} from './slices/createServerDataActions';

/**
 * Complete reactive state slice for a single EagGrid instance.
 *
 * ## Why a store instead of props?
 *
 * EagGrid's component tree is deep: EagGridTable → GridBody → GridRow →
 * GridCell, and separately EagGridTable → GridHeader → toolbar buttons,
 * plus Sidebar → FiltersPanel / ColumnsPanel / SettingsPanel.
 *
 * Without a store, every piece of shared state (loading flag, selected rows,
 * active panel, drag state, …) would have to be passed as props through every
 * intermediate layer that doesn't need it — classic "prop drilling".  The store
 * solves this:
 *
 * - **Leaf components** (GridCell, HeaderCell, skeleton rows) subscribe
 *   only to the slices they care about via `useEagGridSelector(selectX)`.
 * - **Sibling subtrees** (sidebar panels, header toolbar) share state without any
 *   common ancestor having to pass it down.
 * - **Filter UI**: FilterPanel and FilterContentArea all need
 *   access to `filterValues` and `activePanel`.  Passing these through EagGridTable →
 *   Sidebar → FilterPanel → FilterContentArea → … would require 4+ layers
 *   of props that carry no value for the intermediate components.  Instead each
 *   component reads from the store directly.
 *
 * ## Instance isolation
 *
 * `createEagGridStore` is a factory — each `<EagGrid>` mount creates its own
 * independent store instance via `EagGridStoreProvider`.  Multiple grids on the
 * same page cannot bleed state into each other.
 *
 * ## Subscription model
 *
 * The store is powered by Zustand (`zustand/vanilla`).  The factory returns
 * a standard `StoreApi<EagGridState>` extended with named action methods
 * (`store.setPage(3)`) so mutations are explicit and discoverable.
 */

/** Fetch lifecycle for server data. Transitioned only via startFetch/settleFetch
 * so previousRows can never leak past a terminal state. */
export type FetchState = 'idle' | 'loading' | 'success' | 'error';

export interface EagGridState {
  // Static config — set once at mount via initStaticConfig, never changes
  gridCode: string;
  presetInstanceId: string | Nullish;
  disableAllActions: boolean;
  /** When true, refetches keep previous rows visible instead of showing skeletons. */
  hasSilentRefetching: boolean;
  /** Horizontal alignment of the row action buttons within the actions column. */
  actionsAlignment: ActionsAlignment | Nullish;

  // Core grid state
  dataQuery: GetDataQueryResponse | Nullish;
  definition: TransformedDefinitionApiResponse | Nullish;
  apiError: string | Nullish;

  // Preset state
  activePreset: ActivePreset | Nullish;
  customPresets: Preset[] | null;
  virtualPreset: VirtualPreset | Nullish;
  isPresetChanged: boolean;
  presetDisplayModeOverride: PresetDisplayMode | Nullish;
  isVirtualPresetPinned: boolean;
  isPresetCopyInProgress: boolean;
  activePresetDataQuery: GetDataQueryResponse | Nullish;

  /** Live grid settings — the user's working copy. Seeded from activePreset.gridSettings
   *  on every setActivePreset; activePreset.gridSettings stays the SAVED baseline. */
  gridSettings: FeGridSettings | null;

  // Column state
  columnStates: EagColumnState[];

  // Server data state
  rows: RowData[];
  /** Request that produced rows. Export preparation reuses this rather than live filter state. */
  loadedPageRequest: LoadedGridPageRequest | null;
  previousRows: RowData[];
  totalCount: number;
  /** Query identity that produced totalCount. Differs from queryId while a new count is loading. */
  totalCountQueryId: string;
  fetchState: FetchState;
  queryId: string;
  /** Summary row cells keyed by column key, fetched from /data/{queryId}/summary. null = not loaded or disabled. */
  summaryRowCells: Record<string, CellsV5> | null;
  /** Column keys with an in-flight delta fetch (newly-shown columns whose cells
   *  are still loading). Drives per-cell skeletons in GridCell. Separate from
   *  fetchState — the delta fetch is deliberately silent (no grid-wide loading). */
  pendingColumnKeys: string[];
  /** Ref-count per pending column key: one count per in-flight delta fetch.
   *  Overlapping fetches for the same column (show → hide → show) each hold a
   *  count, and the key leaves pendingColumnKeys only when the LAST one settles. */
  pendingColumnCounts: Record<string, number>;

  // Filter state
  filterValues: Filters;
  /** Static filters applied by the host via queryModifier — excluded from "is filter active" check. */
  staticFilters: QueryFilterObject;

  // Selection state
  selectedRowIds: Set<string>;
  bulkSelectionQueryId: string | null;
  bulkActions: DataGridBulkActionsResponseBody | undefined;

  // Keyboard focus navigation state
  /** Which part of the grid currently owns keyboard focus. null = focus is in SmartSearch or outside the grid. */
  focusZone: 'row' | 'actions' | null;
  /** Index (within the current page's visible rows) of the keyboard-focused row. */
  focusedRowIndex: number | null;
  /** Index of the focused action button within the focused row's actions. */
  focusedActionIndex: number;
  /** Index of the keyboard-focused column (within the row's visible data cells). */
  focusedColumnIndex: number | null;

  // UI state
  isMaximized: boolean;
  page: number;
  activePanel: ToolpanelType | null;
  /** True after the first applyPresetColumns call — columns have been hydrated from the preset.
   *  Used to suppress the header row and column-aware skeleton until columns are stable. */
  isColumnsReady: boolean;
  isResizing: boolean;
  resizingColumn: string | Nullish;

  // Drag state
  activeDragId: string | Nullish;
  overColumnId: string | Nullish;
  dropSide: 'left' | 'right' | Nullish;

  /** Cache of loaded choice filter options, keyed by column key.
   *  Survives panel close/reopen — populated by useLazyChoiceFilterOptions,
   *  shared across filter panel components. */
  choiceFilterOptionsCache: Map<string, ChoiceFilterOptions>;

  /** Cache of resolved suggestion (MULTI_CHOICE_ASYNC) labels, keyed by suggestId
   *  then by option value. Populated by useResolveSuggestionFilterLabels (and the
   *  SuggestionFilter editor via write-through) and read by the active-filter bar so
   *  its chips can show a human label instead of the raw stored key (e.g. a branch UUID).
   *  Shared across all entry points — survives panel close/reopen. */
  suggestionLabelsCache: Map<string, Map<string, string>>;

  // Action lock state — per-row, per-action loading/disabled tracking
  actionLocks: Map<string, number>;

  // Bulk action loading — true while a bulk action Promise is pending
  isBulkActionLoading: boolean;

  /** Selected cell range for status bar aggregation and clipboard copy. null = no selection. */
  selectedCellRange: SelectedCellRange | null;
}

/**
 * Public API surface of the store returned by `createEagGridStore`.
 *
 * Extends Zustand's `StoreApi<EagGridState>` with named action methods.
 * Components obtain the store via `useEagGridStore()` (which reads from
 * `EagGridStoreContext`) and interact with it through the named action methods
 * rather than calling `setState` directly.  This keeps mutations explicit and
 * makes it obvious which component owns which state transition.
 *
 * Selectors (the `selectX` functions at the bottom of this file) project a slice
 * of `EagGridState` and are used with `useEagGridSelector(selectX)` to subscribe
 * components to only the state they need — avoiding unnecessary re-renders.
 */
export interface EagGridStore extends StoreApi<EagGridState> {
  // Column actions
  setColumnStates: (columnStates: EagColumnState[]) => void;
  setColumnWidth: (key: string, width: number) => void;
  setColumnHidden: (key: string, hidden: boolean) => void;
  setColumnPinned: (key: string, pinned: 'LEFT' | 'RIGHT' | null) => void;
  reorderColumnsByKey: (fromKey: string, toKey: string) => void;
  /** Moves a contiguous block of columns (group-header drag) before/after the target column. */
  reorderColumnBlock: (blockKeys: string[], targetKey: string, side: 'left' | 'right') => void;
  setColumnSort: (key: string, sort: 'ASC' | 'DESC' | null) => void;
  applyMultiSort: (entries: import('../utils/getSortModel').SortModelEntry[]) => void;
  removeSortColumn: (key: string) => void;
  /** Clears sort + sortIndex from every column in one update (single refetch). */
  clearSortModel: () => void;
  applyPresetColumns: (columns: ColumnsSetting[]) => void;
  applyPresetColumnsAndSort: (columns: ColumnsSetting[]) => void;

  // Server data actions
  setRows: (rows: RowData[], loadedPageRequest?: LoadedGridPageRequest) => void;
  setTotalCount: (totalCount: number, queryId?: string) => void;
  setBulkActions: (bulkActions: DataGridBulkActionsResponseBody | undefined) => void;
  setSummaryRowCells: (cells: Record<string, CellsV5> | null) => void;
  /** Begins a loading cycle. Snapshots current rows into previousRows so the
   * grid can keep rendering them while the next page loads. */
  startFetch: () => void;
  /** Ends a loading cycle: sets the terminal state and clears previousRows.
   * The only way out of 'loading' — enforces the previousRows invariant. */
  settleFetch: (target: Exclude<FetchState, 'loading'>) => void;
  /**
   * Merges partial cell patches into existing rows, matched by id, without a
   * reload. Used to fill in a newly-shown column's data on the current page:
   * matched rows get a new reference (so they re-render), unmatched rows keep
   * their identity, and the rows array is left untouched when nothing matches.
   */
  mergeRowCells: (patchByRowId: Map<string, Partial<RowData>>) => void;
  setQueryId: (queryId: string) => void;
  /** Marks columns as having an in-flight delta fetch. Ref-counted: each call adds
   *  one count per key, so overlapping fetches for the same column stack. */
  addPendingColumns: (keys: string[]) => void;
  /** Releases one pending count per key (fetch settled: success, error, or abort);
   *  the key stops being pending only when every overlapping fetch has released. */
  removePendingColumns: (keys: string[]) => void;

  // Filter actions
  setFilterValues: (filterValues: Filters) => void;
  setStaticFilters: (staticFilters: QueryFilterObject) => void;
  /** Atomically reset ALL filter values (smart search included) and show loading indicator. */
  resetFilterValues: () => void;

  // Selection actions
  setSelectedRowIds: (selectedRowIds: Set<string>) => void;
  selectAllFiltered: () => void;
  toggleRowSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  deselectAll: () => void;
  /** Atomic single-select: clears all selections and selects the given ID.
   *  If the ID is already the only selection, deselects it (toggle behavior). */
  setSingleSelection: (id: string) => void;

  // Settings actions
  setGridSettings: (settings: Partial<FeGridSettings>) => void;

  // UI actions
  setIsMaximized: (isMaximized: boolean) => void;
  /** Capture browser state before React changes the grid's layout and clamps scroll offsets. */
  subscribeBeforeMaximizationChange: (listener: (isMaximized: boolean) => void) => VoidFunction;
  setPresetDisplayMode: (mode: PresetDisplayMode) => void;
  setVirtualPresetPinned: (isPinned: boolean) => void;
  setPage: (page: number) => void;
  setActivePanel: (activePanel: ToolpanelType | null) => void;
  setIsResizing: (isResizing: boolean) => void;
  setResizingColumn: (resizingColumn: string | Nullish) => void;
  startResizing: (colKey: string) => void;
  endResizing: () => void;

  // Drag actions
  setActiveDragId: (activeDragId: string | Nullish) => void;
  setDragOver: (overColumnId: string | Nullish, dropSide: 'left' | 'right' | Nullish) => void;
  clearDragState: () => void;
  setChoiceFilterOptions: (columnKey: string, options: ChoiceFilterOptions) => void;
  setSuggestionLabels: (suggestId: string, entries: SuggestionsOptions) => void;

  // Preset actions
  setActivePreset: (activePreset: ActivePreset | Nullish) => void;
  /** Atomic preset transition: queryId + activePreset land in ONE setState so store
   * reactions never observe the half-switched state (see createDataConfigActions). */
  applyActivePresetTransition: (queryId: string, activePreset: ActivePreset) => void;
  setCustomPresets: (customPresets: Preset[] | null) => void;
  setVirtualPreset: (virtualPreset: VirtualPreset | Nullish) => void;
  setIsPresetChanged: (isPresetChanged: boolean) => void;
  setIsPresetCopyInProgress: (isPresetCopyInProgress: boolean) => void;
  setActivePresetDataQuery: (activePresetDataQuery: GetDataQueryResponse | Nullish) => void;

  // Core actions
  /** Replaces the dataQuery object. GridDataEngine treats a changed identity as a
   * preset switch (reuses the preset's dataQueryId instead of minting a new query) —
   * only call this from preset activation/deactivation or the cold-mount loader. */
  setDataQuery: (dataQuery: GetDataQueryResponse | Nullish) => void;
  setDefinition: (definition: TransformedDefinitionApiResponse | Nullish) => void;
  setApiError: (apiError: string | Nullish) => void;

  // Action lock actions
  lockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => void;
  unlockAction: (rowId: string, actionKey: string, reason: ActionLockReason) => void;
  isActionLocked: (rowId: string, actionKey: string) => boolean;
  clearPromiseLocks: () => void;
  clearLocksForRow: (rowId: string) => void;

  // Bulk action loading
  setIsBulkActionLoading: (loading: boolean) => void;

  // Status bar cell range selection
  setSelectedCellRange: (range: SelectedCellRange | null) => void;

  // Keyboard focus navigation actions
  enterRows: () => void;
  moveRow: (delta: number, rowCount: number) => void;
  enterActions: () => void;
  moveAction: (delta: number, actionCount: number) => void;
  exitToSearch: () => void;
  clearKeyboardFocus: () => void;
  clampFocusedRow: (rowCount: number) => void;
  /** Sync focus state to a specific row's action button — used when DOM focus arrives outside
   *  the keyboard handler (mouse click, focus restoration) so the store matches reality. */
  focusActionsAt: (rowIndex: number, actionIndex: number) => void;
  moveColumn: (delta: number, columnCount: number) => void;
  focusCellAt: (rowIndex: number, columnIndex: number) => void;

  // Static config — call once on mount, never again
  initStaticConfig: (config: {
    gridCode: string;
    presetInstanceId?: string;
    disableAllActions?: boolean;
    hasSilentRefetching?: boolean;
    actionsAlignment?: ActionsAlignment;
  }) => void;
}

/**
 * Factory that creates a new, isolated EagGrid store for a single grid instance.
 *
 * Call once per `<EagGrid>` mount inside `EagGridStoreProvider`.  The returned
 * object is stored in a React ref so it survives re-renders without re-creating
 * the store.
 *
 * @param initialState - Optional partial override for the initial state (used in
 *   tests and for Storybook hydration scenarios).
 */
export function createEagGridStore(initialState?: Partial<EagGridState>): EagGridStore {
  const store = createStore<EagGridState>()(() => ({
    gridCode: '',
    presetInstanceId: null,
    disableAllActions: false,
    hasSilentRefetching: false,
    actionsAlignment: null,
    dataQuery: null,
    definition: null,
    apiError: null,
    activePreset: null,
    customPresets: null, // null = not yet loaded; [] = loaded with no presets
    virtualPreset: null,
    isPresetChanged: false,
    presetDisplayModeOverride: null,
    isVirtualPresetPinned: false,
    isPresetCopyInProgress: false,
    activePresetDataQuery: null,
    gridSettings: initialState?.activePreset?.gridSettings ?? null,
    columnStates: [], // fresh array per factory call
    rows: [],
    loadedPageRequest: null,
    previousRows: [],
    totalCount: 0,
    totalCountQueryId: '',
    fetchState: 'loading', // loading until first fetch settles — prevents empty-state flash
    queryId: '',
    summaryRowCells: null,
    pendingColumnKeys: [],
    pendingColumnCounts: {},
    filterValues: {},
    staticFilters: {},
    bulkSelectionQueryId: null,
    bulkActions: undefined,
    selectedRowIds: new Set<string>(), // fresh Set per factory call
    page: 0,
    isMaximized: false,
    activePanel: null,
    isColumnsReady: false,
    isResizing: false,
    resizingColumn: null,
    activeDragId: null,
    overColumnId: null,
    dropSide: null,
    choiceFilterOptionsCache: new Map(),
    suggestionLabelsCache: new Map(),
    actionLocks: new Map(),
    isBulkActionLoading: false,
    selectedCellRange: null,
    focusZone: null,
    focusedRowIndex: null,
    focusedActionIndex: 0,
    focusedColumnIndex: null,
    ...initialState,
  }));

  // Keyboard-nav first: setRows (server-data slice) clamps focus through it.
  const keyboardNavActions = createKeyboardNavActions(store);

  // Omit<…> validates at compile time that the slices add up to the full
  // EagGridStore action surface — a missing action is a type error here.
  const actions: Omit<EagGridStore, keyof StoreApi<EagGridState>> = {
    ...keyboardNavActions,
    ...createColumnActions(store),
    ...createServerDataActions(store, keyboardNavActions.clampFocusedRow),
    ...createDataConfigActions(store),
    ...createSelectionActions(store, selectAllFilteredSelected),
    ...createResizeActions(store),
    ...createDragActions(store),
    ...createMiscUiActions(store),
    ...createCoreActions(store),
    ...createActionLockActions(store),
  };

  return Object.assign(store, actions);
}

// Selector functions — each takes EagGridState and returns a derived value.
// Use with useEagGridSelector: useEagGridSelector(selectPage)
export const selectDataQuery = (state: EagGridState) => state.dataQuery;
export const selectDefinition = (state: EagGridState) => state.definition;
export const selectApiError = (state: EagGridState) => state.apiError;
export const selectActivePreset = (state: EagGridState) => state.activePreset;
export const selectCustomPresets = (state: EagGridState) => state.customPresets;
export const selectVirtualPreset = (state: EagGridState) => state.virtualPreset;
export const selectIsPresetChanged = (state: EagGridState) => state.isPresetChanged;
export const selectIsPresetCopyInProgress = (state: EagGridState) => state.isPresetCopyInProgress;
export const selectActivePresetDataQuery = (state: EagGridState) => state.activePresetDataQuery;
export const selectColumnStates = (state: EagGridState) => state.columnStates;
export const selectRows = (state: EagGridState) => state.rows;
export const selectTotalCount = (state: EagGridState) => state.totalCount;
export const selectBulkActions = (state: EagGridState) => state.bulkActions;
export const selectHasBulkActions = (state: EagGridState) =>
  (state.bulkActions?.actions.length ?? 0) > 0;
export const selectHasCurrentTotalCount = (state: EagGridState) =>
  state.queryId !== '' && state.totalCountQueryId === state.queryId;
export const selectIsLoading = (state: EagGridState) => state.fetchState === 'loading';
export const selectFetchState = (state: EagGridState) => state.fetchState;
export const selectHasFetchError = (state: EagGridState) => state.fetchState === 'error';
export const selectQueryId = (state: EagGridState) => state.queryId;
export const selectFilterValues = (state: EagGridState) => state.filterValues;
export const selectAllFilteredSelected = (state: EagGridState) =>
  state.bulkSelectionQueryId !== null && state.bulkSelectionQueryId === state.queryId;

export const selectSelectedRowIds = (state: EagGridState) => state.selectedRowIds;
export const selectPage = (state: EagGridState) => state.page;
export const selectActivePanel = (state: EagGridState) => state.activePanel;
export const selectIsResizing = (state: EagGridState) => state.isResizing;
export const selectResizingColumn = (state: EagGridState) => state.resizingColumn;
export const selectPreviousRows = (state: EagGridState) => state.previousRows;
export const selectHasSilentRefetching = (state: EagGridState) => state.hasSilentRefetching;
export const selectGridSettings = (state: EagGridState) => state.gridSettings;
export const selectHorizontalSeparatorsVisible = (state: EagGridState) =>
  state.gridSettings?.horizontalSeparatorsVisible ?? true;
export const selectVerticalSeparatorsVisible = (state: EagGridState) =>
  state.gridSettings?.verticalSeparatorsVisible ?? false;
export const selectColumnGroupsVisible = (state: EagGridState) =>
  state.gridSettings?.columnGroupsVisible ?? false;
export const selectSummaryRowCells = (state: EagGridState) => state.summaryRowCells;
export const selectActiveDragId = (state: EagGridState) => state.activeDragId;
export const selectIsColumnsReady = (state: EagGridState) => state.isColumnsReady;
export const selectIsBulkActionLoading = (state: EagGridState) => state.isBulkActionLoading;
export const selectChoiceFilterOptionsCache = (state: EagGridState) =>
  state.choiceFilterOptionsCache;
export const selectFocusZone = (state: EagGridState) => state.focusZone;
export const selectFocusedRowIndex = (state: EagGridState) => state.focusedRowIndex;
export const selectFocusedActionIndex = (state: EagGridState) => state.focusedActionIndex;
export const selectSelectedCellRange = (state: EagGridState) => state.selectedCellRange;

/** Filter values excluding any static filters injected by queryModifier. */
export const selectFilterValuesWithoutStaticFilters = (state: EagGridState): Filters =>
  omit(keys(state.staticFilters) as string[], state.filterValues) as Filters;

// Static config selectors
export const selectGridCode = (state: EagGridState): string => state.gridCode;

// Per-column selector factory with stable function references via Map cache.
const choiceFilterOptionsSelectorCache = new Map<
  string,
  (state: EagGridState) => ChoiceFilterOptions | undefined
>();

export function selectChoiceFilterOptions(
  columnKey: string
): (state: EagGridState) => ChoiceFilterOptions | undefined {
  let selector = choiceFilterOptionsSelectorCache.get(columnKey);
  if (!selector) {
    selector = (state: EagGridState) => state.choiceFilterOptionsCache.get(columnKey);
    choiceFilterOptionsSelectorCache.set(columnKey, selector);
  }
  return selector;
}

/** The whole suggestion-label cache (outer Map keyed by suggestId). Reference is
 *  stable until setSuggestionLabels replaces it, so it is safe to read via
 *  useEagGridSelector without producing a new reference each snapshot. */
export const selectSuggestionLabelsCache = (
  state: EagGridState
): Map<string, Map<string, string>> => state.suggestionLabelsCache;

export const selectActionLocks = (state: EagGridState) => state.actionLocks;
